import { useMemo, useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { useData } from '../lib/data.jsx';
import { useUI, Sec, Row, Pimg } from '../lib/ui.jsx';
import { rpc } from '../lib/supabase.js';
import { B, todayISO, parseD, iso, thDate, TH_M, TH_D, diffDays } from '../lib/util.js';

export const BK_LABEL = { out: 'กำลังเช่า', booked: 'จองแล้ว', maint: 'ปิดซ่อม' };

// ---------- drawer รายละเอียดการจอง (ใช้ร่วมกับหน้า Fleet) ----------
export function useBookingDrawer() {
  const { contracts, customers, units } = useData();
  const { openDrawer, closeDrawer, toast } = useUI();
  const { can } = useAuth();
  const { refresh } = useData();

  const openBooking = (b) => {
    const from = b.from_date, to = b.to_date;
    const nights = diffDays(to, from);
    const today = todayISO();
    const started = from <= today;
    const over = b.kind === 'out' && to < today;
    const rc = contracts.find(c => c.id === b.contract_id);
    const cancel = async () => {
      if (!confirm(`ยกเลิกการจอง ${b.ref_no}?`)) return;
      try {
        await rpc('rp_cancel_booking', { p_id: b.id, p_reason: null });
        toast('ยกเลิกการจองแล้ว'); closeDrawer(); await refresh();
      } catch (e) { toast(e.message); }
    };
    openDrawer(BK_LABEL[b.kind] + ' · ' + b.ref_no, b.name + ' · ' + b.unit_code, (<>
      <Sec>การจอง</Sec>
      <Row k="สถานะ" v={BK_LABEL[b.kind] + (over ? ' · เกินกำหนด' : '')} gold={over} />
      <Row k="เลขที่อ้างอิง" v={b.ref_no} />
      <Row k="ผู้เช่า" v={b.customer_name || '—'} />
      <Sec>ระยะเวลา</Sec>
      <Row k="วันรับ" v={TH_D[parseD(from).getDay()] + ' ' + thDate(from)} />
      <Row k="กำหนดคืน" v={TH_D[parseD(to).getDay()] + ' ' + thDate(to)} />
      <Row k="จำนวนคืน" v={nights + ' คืน'} />
      <Row k={started ? 'ผ่านไปแล้ว' : 'อีก'} v={Math.abs(diffDays(today, from)) + ' วัน'} />
      {rc && <><Sec>สัญญา</Sec><Row k="มัดจำที่ถือไว้" v={B(rc.deposit) + ' ฿'} gold /></>}
      {(b.kind === 'booked' || b.kind === 'maint') && can('rent.book') && (
        <div className="dw-act">
          <button onClick={cancel}>{b.kind === 'maint' ? 'เปิดใช้งานชิ้นนี้ (จบงานซ่อม)' : 'ยกเลิกการจองนี้'} <span className="ar">✕</span></button>
        </div>
      )}
    </>));
  };
  return openBooking;
}

// ---------- ฟอร์มรับจองล่วงหน้า / ปิดซ่อม ----------
export function BookingForm({ preUnit, onDone }) {
  const { units, freeUnitsRange, isFreeRange } = useData();
  const { toast } = useUI();
  const { refresh } = useData();
  const [kind, setKind] = useState('booked');
  const [unitId, setUnitId] = useState(preUnit || '');
  const [from, setFrom] = useState(todayISO());
  const [nights, setNights] = useState(2);
  const [who, setWho] = useState('');
  const [busy, setBusy] = useState(false);
  const to = iso((() => { const d = parseD(from); d.setDate(d.getDate() + Math.max(1, +nights || 1)); return d; })());
  const free = unitId ? isFreeRange(unitId, from, to) : true;

  const save = async () => {
    if (!unitId) { toast('เลือกอุปกรณ์ก่อน'); return; }
    if (kind === 'booked' && !who.trim()) { toast('ใส่ชื่อลูกค้าที่จอง'); return; }
    setBusy(true);
    try {
      await rpc('rp_add_booking', {
        p: { unit_id: unitId, kind, from_date: from, to_date: to, customer_name: who || null },
      });
      toast(kind === 'maint' ? 'ปิดซ่อมชิ้นนี้แล้ว' : 'รับจองแล้ว — ลงปฏิทินเรียบร้อย');
      await refresh(); onDone?.();
    } catch (e) { toast(e.message); }
    setBusy(false);
  };

  return (<>
    <Sec>เพิ่มรายการในปฏิทิน</Sec>
    <div className="catfilter" style={{ marginBottom: 12 }}>
      <button className={kind === 'booked' ? 'on' : ''} onClick={() => setKind('booked')}>จองล่วงหน้า</button>
      <button className={kind === 'maint' ? 'on' : ''} onClick={() => setKind('maint')}>ปิดซ่อม</button>
    </div>
    <label className="mini">อุปกรณ์ (รายชิ้น)</label>
    <select style={{ width: '100%' }} value={unitId} onChange={e => setUnitId(e.target.value)}>
      <option value="">— เลือก —</option>
      {units.map(u => <option key={u.id} value={u.id}>{u.name} · {u.unit_code} ({u.sku})</option>)}
    </select>
    <div className="formgrid">
      <div><label className="mini">ตั้งแต่วันที่</label><input type="date" className="mono-in" value={from} onChange={e => setFrom(e.target.value || todayISO())} /></div>
      <div><label className="mini">จำนวนคืน</label><input type="number" className="mono-in" min={1} value={nights} onChange={e => setNights(e.target.value)} /></div>
      {kind === 'booked' && <div className="span2"><label className="mini">ชื่อลูกค้า</label><input value={who} onChange={e => setWho(e.target.value)} placeholder="ชื่อผู้จอง" /></div>}
    </div>
    <div className="hint" style={{ marginTop: 8 }}>
      {from && `${thDate(from)} → ${thDate(to)}`} · {free ? 'ช่วงนี้ว่าง' : <b style={{ color: 'var(--gold)' }}>ช่วงนี้ไม่ว่าง — ระบบจะไม่ให้บันทึกซ้อน</b>}
    </div>
    <button className="btn" style={{ marginTop: 12 }} disabled={busy || !free} onClick={save}>
      {kind === 'maint' ? 'บันทึกปิดซ่อม' : 'บันทึกการจอง'}
    </button>
  </>);
}

// ---------- หน้า ปฏิทินคลัง (timeline ทั้งเดือน) ----------
export default function CalendarPage() {
  const { units, bookingOn, bookingsOfUnit } = useData();
  const { openDrawer } = useUI();
  const { can } = useAuth();
  const [offset, setOffset] = useState(0);
  const [cat, setCat] = useState('ทั้งหมด');
  const [showAll, setShowAll] = useState(false);
  const openBooking = useBookingDrawer();

  const today = todayISO();
  const t = parseD(today);
  const base = new Date(t.getFullYear(), t.getMonth() + offset, 1);
  const y = base.getFullYear(), m = base.getMonth();
  const days = new Date(y, m + 1, 0).getDate();

  // เตรียมข้อมูลคอลัมน์วันครั้งเดียวต่อเดือน — เดิมสร้าง Date + จัดรูปวันที่ใหม่ทุกช่อง
  // (190 แถว × 31 วัน ≈ 5,900 ช่อง) ทำให้เปลี่ยนเดือนทีค้างเป็นวินาที
  const cols = useMemo(() => [...Array(days)].map((_, i) => {
    const d = new Date(y, m, i + 1);
    const ds = iso(d);
    return { ds, num: d.getDate(), dow: TH_D[d.getDay()], lbl: thDate(ds),
             wk: d.getDay() === 0 || d.getDay() === 6, td: ds === today };
  }), [y, m, days, today]);

  const monthFrom = cols[0]?.ds, monthTo = cols[cols.length - 1]?.ds;

  // ค่าเริ่มต้นแสดงเฉพาะชิ้นที่มีคิวเดือนนี้ — 190 แถวรวดอ่านไม่รู้เรื่องและเรนเดอร์ช้า
  const rows = useMemo(() => units.filter(u => {
    if (cat !== 'ทั้งหมด' && u.category !== cat) return false;
    if (showAll) return true;
    return bookingsOfUnit(u.id).some(b => !(monthTo < b.from_date || monthFrom > b.to_date));
  }), [units, cat, showAll, bookingsOfUnit, monthFrom, monthTo]);

  const cats = useMemo(() => ['ทั้งหมด', ...new Set(units.map(u => u.category))], [units]);
  const shownCats = useMemo(() => [...new Set(rows.map(u => u.category))], [rows]);

  return (<>
    <h2>ปฏิทินคลังเช่า</h2>
    <div className="hint">ดูภาพรวมว่าชิ้นไหนว่างวันไหน · กดช่องสีเพื่อดูรายละเอียดการจอง</div>
    <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', margin: '16px 0 12px' }}>
      <div className="calnav" style={{ width: 300, marginBottom: 0 }}>
        <button onClick={() => setOffset(o => o - 1)}>‹</button>
        <span className="m">{TH_M[m]} {y + 543}</span>
        <button onClick={() => setOffset(o => o + 1)}>›</button>
        <button style={{ width: 'auto', padding: '0 10px', fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700 }} onClick={() => setOffset(0)}>วันนี้</button>
      </div>
      {can('rent.book') && (
        <button className="btn ghost sm" onClick={() => openDrawer('ปฏิทิน · BOOKING', 'จองล่วงหน้า / ปิดซ่อม', <BookingForm />)}>
          + จองล่วงหน้า / ปิดซ่อม
        </button>
      )}
    </div>
    <div className="pickbar" style={{ marginTop: 0 }}>
      <select value={cat} onChange={e => setCat(e.target.value)} style={{ maxWidth: 320 }}>
        {cats.map(c => <option key={c} value={c}>{c === 'ทั้งหมด' ? 'ทุกหมวด' : c}</option>)}
      </select>
      <label className="chk">
        <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
        แสดงทุกชิ้น (ปกติแสดงเฉพาะชิ้นที่มีคิวเดือนนี้)
      </label>
      <span className="hint" style={{ margin: 0, whiteSpace: 'nowrap' }}>{rows.length} ชิ้น</span>
    </div>
    <div className="callegend" style={{ marginBottom: 12 }}>
      <span><i />ว่าง</span><span><i className="out" />กำลังเช่า</span>
      <span><i className="booked" />จองแล้ว</span><span><i className="maint" />ปิดซ่อม</span>
    </div>
    <div className="tlwrap">
      <table className="tl">
        <thead>
          <tr>
            <th className="unit" rowSpan={2}>อุปกรณ์ · {days} วัน</th>
            {cols.map(c => (
              <th key={c.ds} className={'dow ' + (c.td ? 'td0' : c.wk ? 'wk' : '')}>{c.dow}</th>
            ))}
          </tr>
          <tr>
            {cols.map(c => (
              <th key={c.ds} className={c.td ? 'td0' : c.wk ? 'wk' : ''}>{c.num}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shownCats.map(c => (
            <FragmentRows key={c} cat={c} units={rows.filter(u => u.category === c)}
              cols={cols} days={days} bookingOn={bookingOn} openBooking={openBooking} />
          ))}
          {!rows.length && (
            <tr><td className="unit" colSpan={days + 1}>
              <span className="empty" style={{ padding: 0 }}>
                ไม่มีชิ้นที่มีคิวในเดือนนี้ — ติ๊ก "แสดงทุกชิ้น" เพื่อดูทั้งคลัง
              </span>
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  </>);
}

function FragmentRows({ cat, units, cols, days, bookingOn, openBooking }) {
  return (<>
    <tr className="catrow"><td className="unit" colSpan={days + 1}>{cat}</td></tr>
    {units.map(u => (
      <tr key={u.id}>
        <td className="unit">
          <div className="nm">{u.name} · {u.unit_code}</div>
          <div className="sk">{u.sku}</div>
        </td>
        {cols.map(c => {
          const b = bookingOn(u.id, c.ds);
          return (
            <td key={c.ds}
              className={'d ' + (b ? b.kind : '') + (c.wk ? ' wk' : '')}
              style={b ? { cursor: 'pointer' } : undefined}
              title={`${u.name} ${u.unit_code} — ${c.lbl} — ${b ? `${BK_LABEL[b.kind]} · ${b.customer_name || ''} (${b.ref_no})` : 'ว่าง'}`}
              onClick={() => b && openBooking(b)} />
          );
        })}
      </tr>
    ))}
  </>);
}
